/*
 * Kunbi Klaviyo tracking utility.
 * Handles:
 *   - Browse abandonment: fires "Viewed Product" event on every PDP load
 *   - Newsletter capture: auto-wires any email input form (outside kunbi-bday popup)
 *     to POST to Klaviyo with metric "Newsletter Subscribe"
 *   - Blog subscribe capture: same handler with metric "Blog Subscribe" if form
 *     lives inside an <article> tag
 *   - Identify cookie: remembers email between visits so browse events attach
 *     to the correct profile → cart / browse abandonment flows work
 * Public API:
 *   window.kunbiKlaviyo.event(metricName, properties, profile)
 *   window.kunbiKlaviyo.identify(email, properties)
 *
 * IMPORTANT: uses PUBLIC company_id only (safe to embed in JS).
 * NEVER put the pk_ private key in this file.
 */
(function () {
  'use strict';

  var COMPANY_ID = 'XGqSi4';
  var IDENTIFY_COOKIE = 'kunbi_klv_email';
  var VIEWED_COOKIE_PREFIX = 'kunbi_vp_';
  var API_REVISION = '2024-10-15';

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[2]) : null;
  }
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }

  function klaviyoEvent(metricName, properties, profileAttrs) {
    var props = properties || {};
    var profile = profileAttrs || {};
    var storedEmail = getCookie(IDENTIFY_COOKIE);
    if (!profile.email && storedEmail) profile.email = storedEmail;
    if (!profile.email) {
      // No identity → skip. Klaviyo needs email or phone to attach event.
      return Promise.resolve({ skipped: true, reason: 'no-identity' });
    }

    var payload = {
      data: {
        type: 'event',
        attributes: {
          properties: props,
          metric: { data: { type: 'metric', attributes: { name: metricName } } },
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: profile.email,
                properties: profile.properties || {}
              }
            }
          }
        }
      }
    };

    return fetch('https://a.klaviyo.com/client/events/?company_id=' + COMPANY_ID, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': API_REVISION
      },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (r.ok || r.status === 202) return true;
      return Promise.reject(r);
    });
  }

  function identify(email, properties) {
    if (!email) return;
    setCookie(IDENTIFY_COOKIE, email, 365);
    var payload = {
      data: {
        type: 'profile',
        attributes: {
          email: email,
          properties: properties || {}
        }
      }
    };
    return fetch('https://a.klaviyo.com/client/profiles/?company_id=' + COMPANY_ID, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': API_REVISION
      },
      body: JSON.stringify(payload)
    }).catch(function () {});
  }

  // ------- Browse abandonment: fire Viewed Product on every PDP load -------
  function trackViewedProduct() {
    // Only on product pages
    var body = document.body;
    if (!body || body.getAttribute('data-template') !== 'product') {
      if (!location.pathname.match(/\/products\//)) return;
    }

    // Priority 1: use theme-injected KUNBI_PRODUCT (set in layout/theme.liquid)
    var product = window.KUNBI_PRODUCT || null;

    // Priority 2: any product-json script the theme rendered
    if (!product) {
      var productEl = document.querySelector('[data-product-json], script[type="application/json"][data-product-json], #ProductJson-product, #product-json');
      if (productEl) {
        try { product = JSON.parse(productEl.textContent || productEl.innerText); } catch (e) {}
      }
    }

    // Priority 3: Shopify's global if the theme populated it
    if (!product && window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      product = window.ShopifyAnalytics.meta.product;
    }

    // Fallback: scrape from meta tags
    if (!product) {
      var titleEl = document.querySelector('meta[property="og:title"]');
      var urlEl = document.querySelector('link[rel="canonical"]');
      var imgEl = document.querySelector('meta[property="og:image"]');
      var priceEl = document.querySelector('meta[property="product:price:amount"], meta[property="og:price:amount"]');
      if (titleEl || urlEl) {
        product = {
          title: titleEl ? titleEl.getAttribute('content') : document.title,
          url: urlEl ? urlEl.getAttribute('href') : location.href,
          featured_image: imgEl ? imgEl.getAttribute('content') : null,
          price: priceEl ? parseFloat(priceEl.getAttribute('content')) : null
        };
      }
    }

    if (!product) return;

    var handle = product.handle || (product.url ? product.url.split('/').pop().split('?')[0] : null) || product.title;
    if (!handle) return;

    // De-dupe: only fire once per 30 minutes per product per browser
    var dedupeKey = VIEWED_COOKIE_PREFIX + handle.substring(0, 40);
    if (getCookie(dedupeKey)) return;
    setCookie(dedupeKey, '1', 30 / 1440); // 30 min

    var priceMinor = null;
    if (typeof product.price === 'number') {
      priceMinor = product.price >= 1000 ? product.price / 100 : product.price;
    } else if (product.price_min) {
      priceMinor = product.price_min / 100;
    }

    var props = {
      ProductName: product.title || product.name,
      ProductID: product.id,
      Handle: handle,
      URL: product.url || location.href,
      ImageURL: product.featured_image || (product.images && product.images[0]) || null,
      Price: priceMinor,
      Vendor: product.vendor
    };

    klaviyoEvent('Viewed Product', props);
  }

  // ------- Newsletter capture: auto-wire any email form outside popup -------
  function wireNewsletterForms() {
    // Find every form that has an email input, is NOT inside our popup / cart / product ATC
    var forms = document.querySelectorAll('form');
    forms.forEach(function (form) {
      // Skip forms inside our own popup
      if (form.closest('#kunbi-bday')) return;
      // Skip Shopify's product-form / cart forms
      if (form.action && (form.action.indexOf('/cart') !== -1 || form.action.indexOf('/checkout') !== -1)) return;
      // Skip search forms
      if (form.getAttribute('role') === 'search') return;

      var emailInput = form.querySelector('input[type="email"]');
      if (!emailInput) return;

      // Only wire once
      if (form.getAttribute('data-kunbi-klv-wired') === '1') return;
      form.setAttribute('data-kunbi-klv-wired', '1');

      form.addEventListener('submit', function (e) {
        var email = emailInput.value.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return; // let native validation handle

        // Determine metric based on location
        var metric = 'Newsletter Subscribe';
        var source = 'Footer';
        if (form.closest('article')) { metric = 'Blog Subscribe'; source = 'Blog'; }
        else if (form.closest('footer')) { source = 'Footer'; }
        else if (form.closest('header')) { source = 'Header'; }

        identify(email);
        klaviyoEvent(metric, { source: source, page_url: location.href }, {
          email: email,
          properties: { last_newsletter_source: source }
        });
        // Don't preventDefault — let the theme's original handler still run (Shopify's contact form, etc.)
      });
    });
  }

  // ------- Back-in-stock: auto-inject "notify me" form when ATC is disabled -----
  function wireBackInStock() {
    if (!location.pathname.match(/\/products\//)) return;
    var soldOutButtons = document.querySelectorAll('button[name="add"][disabled], product-form button[disabled]');
    soldOutButtons.forEach(function (btn) {
      if (btn.getAttribute('data-kunbi-bis-wired') === '1') return;
      btn.setAttribute('data-kunbi-bis-wired', '1');
      var container = btn.closest('product-form') || btn.parentElement;
      if (!container) return;
      if (container.querySelector('.kunbi-bis')) return;

      var wrap = document.createElement('div');
      wrap.className = 'kunbi-bis';
      wrap.setAttribute('style', 'margin-top:12px;padding:14px;background:#FBF7F3;border:1px solid rgba(140,83,87,0.2);border-radius:12px;font-family:"Helvetica Neue",-apple-system,sans-serif;');
      wrap.innerHTML =
        '<p style="margin:0 0 8px;font-size:13px;font-weight:600;color:#212121;letter-spacing:0.02em;">Sold out. Get an email when it’s back.</p>' +
        '<form data-kunbi-bis-form style="display:flex;gap:8px;">' +
          '<input type="email" required placeholder="your@email.com" style="flex:1;min-width:0;padding:10px 12px;border:1px solid rgba(33,33,33,0.18);border-radius:8px;font-size:16px;font-family:inherit;color:#212121;background:#FFFFFF;">' +
          '<button type="submit" style="min-height:44px;padding:0 18px;background:#8C5357;color:#FFFFFF;border:0;border-radius:999px;font-family:inherit;font-size:12px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;cursor:pointer;">Notify me</button>' +
        '</form>' +
        '<p data-kunbi-bis-msg style="margin:8px 0 0;font-size:12px;color:#8C5357;" hidden></p>';
      container.parentElement.insertBefore(wrap, container.nextSibling);

      var form = wrap.querySelector('[data-kunbi-bis-form]');
      var msg = wrap.querySelector('[data-kunbi-bis-msg]');
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var email = form.querySelector('input[type="email"]').value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
        var handle = location.pathname.split('/products/')[1].split('?')[0].split('/')[0];
        var titleEl = document.querySelector('meta[property="og:title"]');
        identify(email);
        klaviyoEvent('Back in Stock Requested', {
          product_handle: handle,
          product_title: titleEl ? titleEl.getAttribute('content') : document.title,
          url: location.href
        }, { email: email });
        form.style.display = 'none';
        msg.hidden = false;
        msg.textContent = 'We’ll email you the moment it’s back.';
      });
    });
  }

  // Public API
  window.kunbiKlaviyo = {
    event: klaviyoEvent,
    identify: identify,
    company_id: COMPANY_ID
  };

  function boot() {
    trackViewedProduct();
    wireNewsletterForms();
    wireBackInStock();
    // Re-wire forms if the DOM changes (newsletters injected late by apps)
    var observer = new MutationObserver(function () { wireNewsletterForms(); wireBackInStock(); });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { observer.disconnect(); }, 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
